import { useCallback, useEffect, useRef } from 'react';
import Hammer from '@egjs/hammerjs';

function useHammerEvents(autoSize, isLoaded, tapEvent, swipeEvent) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hammertime = useRef<any>(null);
  const element = useRef<HTMLImageElement | null>(null);
  const imgContainer = useRef<HTMLElement | null>(null);

  const MIN_SCALE = 1;
  const MAX_SCALE = 10;

  const imageWidth = useRef(0);
  const imageHeight = useRef(0);
  const containerWidth = useRef(0);
  const containerHeight = useRef(0);
  const displayImageX = useRef(0);
  const displayImageY = useRef(0);
  const displayImageScale = useRef(1);

  const displayDefaultWidth = useRef(0);
  const displayDefaultHeight = useRef(0);

  const rangeX = useRef(0);
  const rangeMaxX = useRef(0);
  const rangeMinX = useRef(0);

  const rangeY = useRef(0);
  const rangeMaxY = useRef(0);
  const rangeMinY = useRef(0);

  const displayImageCurrentX = useRef(0);
  const displayImageCurrentY = useRef(0);
  const displayImageCurrentScale = useRef(1);

  const swipeActive = useRef(false);

  const updateRange = () => {
    rangeX.current = Math.max(
      0,
      Math.round(displayDefaultWidth.current * displayImageCurrentScale.current) - containerWidth.current,
    );
    rangeY.current = Math.max(
      0,
      Math.round(displayDefaultHeight.current * displayImageCurrentScale.current) - containerHeight.current,
    );

    rangeMaxX.current = Math.round(rangeX.current / 2);
    rangeMinX.current = 0 - rangeMaxX.current;

    rangeMaxY.current = Math.round(rangeY.current / 2);
    rangeMinY.current = 0 - rangeMaxY.current;
  };

  const clamp = (value, min, max) => Math.min(Math.max(min, value), max);

  const clampScale = useCallback((newScale) => clamp(newScale, MIN_SCALE, MAX_SCALE), []);

  const updateDisplayImage = useCallback((x, y, scale) => {
    const transform = `translateX(${x}px) translateY(${y}px) scale(${scale},${scale})`;

    if (element.current) {
      element.current.style.transform = transform;
      element.current.style.webkitTransform = transform;
      element.current.style.visibility = 'visible';
    }
  }, []);

  const resizeContainer = useCallback(() => {
    containerWidth.current = imgContainer.current?.offsetWidth || 0;
    containerHeight.current = imgContainer.current?.offsetHeight || 0;

    if (displayDefaultWidth.current !== 0 && displayDefaultHeight.current !== 0) {
      displayDefaultWidth.current = element.current?.offsetWidth || 0;
      displayDefaultHeight.current = element.current?.offsetHeight || 0;

      updateRange();

      displayImageCurrentX.current = clamp(displayImageX.current, rangeMinX.current, rangeMaxX.current);
      displayImageCurrentY.current = clamp(displayImageY.current, rangeMinY.current, rangeMaxY.current);

      updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageCurrentScale.current);

      displayImageX.current = displayImageCurrentX.current;
      displayImageY.current = displayImageCurrentY.current;
    }
  }, [updateDisplayImage]);

  useEffect(() => {
    if (!element.current && isLoaded) {
      imgContainer.current = document.querySelector('.img-container');
      element.current = document.querySelector('[data-image=current]');
    }
  }, [element, isLoaded]);

  useEffect(() => {
    if (element.current && imgContainer.current) {
      if (!hammertime.current) {
        // @ts-ignore
        hammertime.current = new Hammer(element.current, {
          cssProps: {
            // enables ios callout menu on touch and hold when wanting to save image
            touchCallout: 'default',
            userDrag: 'none',
            userSelect: 'none',
          },
        });

        hammertime.current.get('pinch').set({ enable: true });
        hammertime.current.get('pan').set({ direction: Hammer.DIRECTION_ALL });
        hammertime.current.get('swipe').set({
          direction: Hammer.DIRECTION_HORIZONTAL,
          threshold: 10,
          velocity: 1,
        });
      }

      if (isLoaded) {
        imageWidth.current = element.current.width;
        imageHeight.current = element.current.height;
        displayDefaultWidth.current = element.current.offsetWidth;
        displayDefaultHeight.current = element.current.offsetHeight;

        rangeX.current = Math.max(0, displayDefaultWidth.current - imgContainer.current.offsetWidth);
        rangeY.current = Math.max(0, displayDefaultHeight.current - imgContainer.current.offsetHeight);

        displayImageX.current = displayDefaultWidth.current / 2;
        displayImageY.current = displayDefaultHeight.current / 2;
        displayImageScale.current = MIN_SCALE;
        displayImageCurrentScale.current = MIN_SCALE;

        resizeContainer();
      }
    }
  }, [resizeContainer, autoSize, isLoaded]);

  const resizeListener = useCallback(() => resizeContainer(), [resizeContainer]);
  const mouseListener = useCallback((e) => e.preventDefault(), []);
  const wheelListener = useCallback(
    (ev) => {
      // Two mouse wheel features depending on autosize:
      // 1. use mouse wheel for panning when auto size is off because large image is shown
      // 2. zoom in feature for when auto size is on because the image is sized down to fit the screen
      if (!autoSize) {
        const isNegativeX = Math.sign(ev.deltaX) === -1;
        const isNegativeY = Math.sign(ev.deltaY) === -1;

        const deltaX = clamp(Math.abs(ev.deltaX), 0, 100);
        const deltaY = clamp(Math.abs(ev.deltaY), 0, 100);

        displayImageCurrentX.current = clamp(
          displayImageX.current + (isNegativeX ? deltaX : -deltaX),
          rangeMinX.current,
          rangeMaxX.current,
        );
        displayImageCurrentY.current = clamp(
          displayImageY.current + (isNegativeY ? deltaY : -deltaY),
          rangeMinY.current,
          rangeMaxY.current,
        );
      } else {
        // fix for firefox, they don't have a wheelDelta for some reason on their event
        const delta = ev.wheelDelta || ev.deltaY * -5;

        displayImageCurrentScale.current = clampScale(displayImageScale.current + delta / 400);
        displayImageScale.current = displayImageCurrentScale.current;

        updateRange();

        displayImageCurrentX.current = clamp(displayImageCurrentX.current, rangeMinX.current, rangeMaxX.current);
        displayImageCurrentY.current = clamp(displayImageCurrentY.current, rangeMinY.current, rangeMaxY.current);
      }

      updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageScale.current);

      displayImageX.current = displayImageCurrentX.current;
      displayImageY.current = displayImageCurrentY.current;
    },
    [autoSize, clampScale, updateDisplayImage],
  );

  useEffect(
    () => () => {
      hammertime.current = null;
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('resize', resizeListener);
    window.addEventListener('mousedown', mouseListener);
    window.addEventListener('wheel', wheelListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
      window.removeEventListener('mousedown', mouseListener);
      window.removeEventListener('wheel', wheelListener);
    };
  }, [mouseListener, resizeListener, wheelListener]);

  if (!element.current || !hammertime.current) {
    return;
  }

  hammertime.current.on('tap', () => {
    tapEvent();
  });

  hammertime.current.on('pan', (ev) => {
    displayImageCurrentX.current = clamp(displayImageX.current + ev.deltaX, rangeMinX.current, rangeMaxX.current);
    displayImageCurrentY.current = clamp(displayImageY.current + ev.deltaY, rangeMinY.current, rangeMaxY.current);

    updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageScale.current);
  });

  hammertime.current.on('pinch pinchmove', (ev) => {
    displayImageCurrentScale.current = clampScale(ev.scale * displayImageScale.current);
    updateRange();
    displayImageCurrentX.current = clamp(displayImageX.current + ev.deltaX, rangeMinX.current, rangeMaxX.current);
    displayImageCurrentY.current = clamp(displayImageY.current + ev.deltaY, rangeMinY.current, rangeMaxY.current);
    updateDisplayImage(displayImageCurrentX.current, displayImageCurrentY.current, displayImageCurrentScale.current);
  });

  hammertime.current.on('panend pancancel pinchend pinchcancel', () => {
    displayImageScale.current = displayImageCurrentScale.current;
    displayImageX.current = displayImageCurrentX.current;
    displayImageY.current = displayImageCurrentY.current;
  });

  hammertime.current.on('swipe', (ev) => {
    if (!swipeActive.current && Math.abs(displayImageCurrentX.current) === rangeMaxX.current) {
      swipeActive.current = true;

      // set timeout to debounce swipe events
      setTimeout(() => {
        swipeActive.current = false;
      }, 500);

      // left arrow takes you back and right arrow takes you forward on the UI, which means...
      // invert the direction, swipe to the left to advance, swipe right to go backward
      swipeEvent.current(ev.direction === Hammer.DIRECTION_LEFT ? 'right' : 'left');
    }
  });
}

export default useHammerEvents;
